import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sprout, TrendingUp, ArrowLeft } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pontuacao = () => {
  const navigate = useNavigate();
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScores();
  }, []);

  const fetchScores = async () => {
    try {
      const res = await axios.get(`${API}/scoring/all`);
      setScores(res.data);
    } catch (error) {
      console.error('Erro ao carregar pontuações:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalIcon = (medalha) => {
    const medals = {
      'ouro': '🥇',
      'prata': '🥈',
      'bronze': '🥉',
      'nenhuma': '⭐'
    };
    return medals[medalha] || medals['nenhuma'];
  };

  const CategoryView = () => (
    <div className="space-y-4">
      {scores.map((score) => (
        <Card
          key={score.child_id}
          data-testid={`score-card-${score.child_id}`}
          onClick={() => navigate(`/participante/${score.child_id}`)}
          className="bg-white rounded-2xl p-5 shadow-md border border-[#E1ECE5] hover:shadow-lg transition-all cursor-pointer"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-nunito font-bold text-xl text-[#1A4D2E]">{score.child_name}</h3>
              <p className="font-inter text-sm text-gray-500">Total: {score.total_pontos} sementes</p>
            </div>
            <span className="text-3xl">{getMedalIcon(score.medalha)}</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-gray-600 text-xs mb-1">Presenças</p>
              <p className="font-nunito font-bold text-lg text-green-700">{score.total_presencas}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-gray-600 text-xs mb-1">Pontualidade</p>
              <p className="font-nunito font-bold text-lg text-blue-700">{score.total_pontualidade}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-gray-600 text-xs mb-1">Bíblia</p>
              <p className="font-nunito font-bold text-lg text-purple-700">{score.total_biblia}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3">
              <p className="text-gray-600 text-xs mb-1">Visitas</p>
              <p className="font-nunito font-bold text-lg text-orange-700">{score.total_visitas}</p>
            </div>
            <div className="bg-pink-50 rounded-lg p-3">
              <p className="text-gray-600 text-xs mb-1">Devocionais</p>
              <p className="font-nunito font-bold text-lg text-pink-700">{score.total_devocionais}</p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3">
              <p className="text-gray-600 text-xs mb-1">Versículos</p>
              <p className="font-nunito font-bold text-lg text-indigo-700">{score.total_versiculos}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 md:col-span-2">
              <p className="text-gray-600 text-xs mb-1">Gincana</p>
              <p className="font-nunito font-bold text-lg text-yellow-700">{score.total_gincana}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3 md:col-span-2 border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-700 text-xs mb-1 font-semibold">🌱 Sementes Extras</p>
                  <p className="font-nunito font-bold text-lg text-emerald-800">
                    {score.extras_sementes || 0}
                    {score.extras_lancamentos > 0 && (
                      <span className="text-xs text-emerald-600 font-normal ml-1">
                        ({score.extras_lancamentos} lançamento{score.extras_lancamentos !== 1 ? 's' : ''})
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const TotalView = () => (
    <div className="space-y-4">
      {scores.map((score) => (
        <Card
          key={score.child_id}
          data-testid={`total-card-${score.child_id}`}
          onClick={() => navigate(`/participante/${score.child_id}`)}
          className="bg-white rounded-2xl p-6 shadow-md border border-[#E1ECE5] hover:shadow-lg transition-all cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-1">
                {score.child_name}
              </h3>
              <p className="font-inter text-sm text-gray-500">Pontuação geral</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 mb-1">
                <Sprout className="w-6 h-6 text-[#F4D35E]" />
                <p className="font-nunito font-bold text-4xl text-[#1A4D2E]">
                  {score.total_pontos}
                </p>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <span className="text-3xl">{getMedalIcon(score.medalha)}</span>
                <p className="font-inter text-sm text-gray-500 capitalize">{score.medalha}</p>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-7 h-7" strokeWidth={2.5} />
              <h1 className="font-nunito font-bold text-3xl">Pontuação</h1>
            </div>
            <img src="/logos/logo_semente.png" alt="Projeto Semente" className="w-36 h-36 drop-shadow-lg" />
          </div>
          <p className="font-inter text-white/80 text-sm">Acompanhe o desempenho de cada criança</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <p className="font-inter text-gray-500">Carregando...</p>
          </div>
        ) : scores.length > 0 ? (
          <Tabs defaultValue="total" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="total" data-testid="tab-total">Pontuação Total</TabsTrigger>
              <TabsTrigger value="category" data-testid="tab-category">Por Categoria</TabsTrigger>
            </TabsList>

            <TabsContent value="total">
              <TotalView />
            </TabsContent>

            <TabsContent value="category">
              <CategoryView />
            </TabsContent>
          </Tabs>
        ) : (
          <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
            <Sprout className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="font-inter text-gray-500 mb-4">Nenhuma pontuação registrada ainda</p>
            <button
              onClick={() => navigate('/cadastro')}
              className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-6 py-3 font-semibold shadow-md active:scale-95 transition-all"
            >
              Cadastrar Primeira Criança
            </button>
          </Card>
        )}
      </div>

      <Navigation active="pontuacao" />
    </div>
  );
};

export default Pontuacao;
